import { useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronUp, Filter, Plus, Save, Search, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { cn } from '@/utils/cn';
import {
  defaultSavedFilterName,
  METRIC_FILTER_OPERATORS,
  parseMetricInput,
  type MetricFilter,
  type MetricFilterOperator,
  type MetricFilterOption,
  type SavedMetricFilterPreset,
} from '@/utils/workspaceMetricFilters';
import type { MetricFiltersController } from '@/components/workspace/useMetricFilters';

type OpenMenu =
  | { type: 'metric'; filterId: string }
  | { type: 'operator'; filterId: string }
  | null;

export function WorkspaceMetricFiltersPopover({
  metrics,
  filters,
  completeFilters,
  savedPresets,
  addFilter,
  updateFilter,
  removeFilter,
  clearFilters,
  savePreset,
  applyPreset,
  removePreset,
}: {
  metrics: readonly MetricFilterOption[];
} & Pick<
  MetricFiltersController,
  | 'filters'
  | 'completeFilters'
  | 'savedPresets'
  | 'addFilter'
  | 'updateFilter'
  | 'removeFilter'
  | 'clearFilters'
  | 'savePreset'
  | 'applyPreset'
  | 'removePreset'
>) {
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const [metricQuery, setMetricQuery] = useState('');
  const [saveNameOpen, setSaveNameOpen] = useState(false);
  const [saveName, setSaveName] = useState('');

  const completeCount = completeFilters.length;
  const defaultName = defaultSavedFilterName(filters, metrics);

  const filteredMetrics = useMemo(() => {
    const q = metricQuery.trim().toLowerCase();
    if (!q) return metrics;
    return metrics.filter(
      (item) => item.label.toLowerCase().includes(q) || item.key.toLowerCase().includes(q)
    );
  }, [metricQuery, metrics]);

  const closeMenus = () => {
    setOpenMenu(null);
    setMetricQuery('');
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      closeMenus();
      setSaveNameOpen(false);
    }
  };

  const startSave = () => {
    setSaveName(defaultName);
    setSaveNameOpen(true);
  };

  const confirmSave = () => {
    savePreset(saveName.trim() || defaultName);
    setSaveNameOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-expanded={open}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-foreground hover:bg-surface-muted"
        >
          <Filter className="h-3.5 w-3.5 text-foreground-muted" aria-hidden />
          Metric Filters
          {completeCount > 0 ? (
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold text-brand-foreground">
              {completeCount}
            </span>
          ) : null}
          {open ? (
            <ChevronUp className="h-3.5 w-3.5 text-foreground-muted" aria-hidden />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-foreground-muted" aria-hidden />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-3" onOpenAutoFocus={(event) => event.preventDefault()}>
        <div className="space-y-2">
          <div>
            <div className="text-body-sm font-semibold text-foreground">Saved Filters:</div>
            {savedPresets.length === 0 ? (
              <p className="mt-1 text-body-sm italic text-foreground-subtle">None</p>
            ) : (
              <ul className="mt-1 space-y-1">
                {savedPresets.map((preset) => (
                  <SavedPresetRow
                    key={preset.id}
                    preset={preset}
                    onApply={() => {
                      applyPreset(preset);
                      closeMenus();
                    }}
                    onRemove={() => removePreset(preset.id)}
                  />
                ))}
              </ul>
            )}
          </div>

          {filters.length > 0 ? (
            <div className="space-y-2 border-t border-border pt-2">
              <div className="text-body-sm font-semibold text-foreground">Current Filters:</div>
              {filters.map((filter) => (
                <FilterRow
                  key={filter.id}
                  filter={filter}
                  metrics={metrics}
                  filteredMetrics={filteredMetrics}
                  metricQuery={metricQuery}
                  onMetricQueryChange={setMetricQuery}
                  openMenu={openMenu}
                  onOpenMenu={(menu) => {
                    setOpenMenu(menu);
                    if (menu?.type !== 'metric') setMetricQuery('');
                  }}
                  onChange={updateFilter}
                  onRemove={() => {
                    removeFilter(filter.id);
                    if (openMenu?.filterId === filter.id) closeMenus();
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="border-t border-border" />
          )}

          {saveNameOpen ? (
            <div className="flex items-center gap-2">
              <input
                value={saveName}
                onChange={(event) => setSaveName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') confirmSave();
                  if (event.key === 'Escape') setSaveNameOpen(false);
                }}
                placeholder="Filter name"
                className="h-8 min-w-0 flex-1 rounded-lg border border-border bg-surface px-2 text-body-sm text-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                autoFocus
              />
              <button
                type="button"
                onClick={confirmSave}
                className="h-8 rounded-lg border border-border bg-surface px-2.5 text-body-sm font-medium hover:bg-surface-muted"
              >
                Save
              </button>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                addFilter();
                closeMenus();
              }}
              className="inline-flex h-8 items-center gap-1 rounded-lg border border-border bg-surface px-2.5 text-body-sm font-medium text-foreground hover:bg-surface-muted"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              Add Filter
            </button>
            {completeCount > 0 && !saveNameOpen ? (
              <button
                type="button"
                onClick={startSave}
                className="inline-flex h-8 items-center gap-1 rounded-lg border border-border bg-surface px-2.5 text-body-sm font-medium text-foreground hover:bg-surface-muted"
              >
                <Save className="h-3.5 w-3.5" aria-hidden />
                Save Filter
              </button>
            ) : null}
            {filters.length > 0 ? (
              <button
                type="button"
                onClick={() => {
                  clearFilters();
                  closeMenus();
                  setSaveNameOpen(false);
                }}
                className="ml-auto text-body-sm font-medium text-error-ink hover:underline"
              >
                Clear All
              </button>
            ) : null}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SavedPresetRow({
  preset,
  onApply,
  onRemove,
}: {
  preset: SavedMetricFilterPreset;
  onApply: () => void;
  onRemove: () => void;
}) {
  return (
    <li className="flex items-center gap-1">
      <button
        type="button"
        onClick={onApply}
        className="min-w-0 flex-1 truncate rounded-md px-1.5 py-1 text-left text-body-sm text-brand hover:bg-brand/10"
      >
        {preset.name}
      </button>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove saved filter ${preset.name}`}
        className="rounded p-1 text-foreground-subtle hover:bg-surface-muted hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

function FilterRow({
  filter,
  metrics,
  filteredMetrics,
  metricQuery,
  onMetricQueryChange,
  openMenu,
  onOpenMenu,
  onChange,
  onRemove,
}: {
  filter: MetricFilter;
  metrics: readonly MetricFilterOption[];
  filteredMetrics: readonly MetricFilterOption[];
  metricQuery: string;
  onMetricQueryChange: (value: string) => void;
  openMenu: OpenMenu;
  onOpenMenu: (menu: OpenMenu) => void;
  onChange: (id: string, patch: Partial<Pick<MetricFilter, 'metric' | 'operator' | 'value'>>) => void;
  onRemove: () => void;
}) {
  const metricLabel = metrics.find((item) => item.key === filter.metric)?.label ?? filter.metric;
  const operator = METRIC_FILTER_OPERATORS.find((item) => item.value === filter.operator);
  const metricOpen = openMenu?.type === 'metric' && openMenu.filterId === filter.id;
  const operatorOpen = openMenu?.type === 'operator' && openMenu.filterId === filter.id;

  return (
    <div className="relative flex items-center gap-1.5">
      <div className="relative min-w-0 flex-1">
        <button
          type="button"
          onClick={() => onOpenMenu(metricOpen ? null : { type: 'metric', filterId: filter.id })}
          className={cn(
            'flex h-8 w-full items-center justify-between gap-1 rounded-lg border bg-surface px-2 text-left text-body-sm',
            metricOpen ? 'border-foreground' : 'border-border'
          )}
        >
          <span className="truncate">{metricLabel}</span>
          {metricOpen ? (
            <ChevronUp className="h-3.5 w-3.5 shrink-0 text-foreground-muted" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-foreground-muted" />
          )}
        </button>
        {metricOpen ? (
          <div className="absolute left-0 top-full z-20 mt-1 w-64 overflow-hidden rounded-xl border border-border bg-surface shadow-lg">
            <div className="relative border-b border-border px-2 py-1.5">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-foreground-subtle" />
              <input
                value={metricQuery}
                onChange={(event) => onMetricQueryChange(event.target.value)}
                placeholder="Search metrics..."
                className="h-8 w-full rounded-md bg-transparent pl-7 pr-2 text-body-sm text-foreground placeholder:text-foreground-subtle focus:outline-none"
                autoFocus
              />
            </div>
            <div className="max-h-56 overflow-y-auto py-1">
              <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-foreground-subtle">
                Core Metrics
              </div>
              {filteredMetrics.length === 0 ? (
                <div className="px-3 py-2 text-body-sm text-foreground-subtle">No metrics</div>
              ) : (
                filteredMetrics.map((item) => {
                  const selected = item.key === filter.metric;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => {
                        onChange(filter.id, { metric: item.key });
                        onOpenMenu(null);
                        onMetricQueryChange('');
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-body-sm',
                        selected ? 'bg-brand/10 font-medium text-brand' : 'text-foreground hover:bg-surface-muted'
                      )}
                    >
                      {selected ? <Check className="h-3.5 w-3.5 shrink-0" /> : <span className="w-3.5" />}
                      {item.label}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        ) : null}
      </div>

      <div className="relative">
        <button
          type="button"
          onClick={() => onOpenMenu(operatorOpen ? null : { type: 'operator', filterId: filter.id })}
          className={cn(
            'flex h-8 w-14 items-center justify-center gap-0.5 rounded-lg border bg-surface text-body-sm font-medium',
            operatorOpen ? 'border-foreground' : 'border-border'
          )}
        >
          {operator?.symbol ?? '>'}
          <ChevronDown className="h-3 w-3 text-foreground-muted" />
        </button>
        {operatorOpen ? (
          <div className="absolute left-0 top-full z-20 mt-1 w-16 overflow-hidden rounded-xl border border-border bg-surface py-1 shadow-lg">
            {METRIC_FILTER_OPERATORS.map((item) => {
              const selected = item.value === filter.operator;
              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => {
                    onChange(filter.id, { operator: item.value as MetricFilterOperator });
                    onOpenMenu(null);
                  }}
                  className={cn(
                    'flex w-full items-center gap-1 px-2 py-1.5 text-body-sm',
                    selected ? 'bg-brand/10 font-medium text-brand' : 'text-foreground hover:bg-surface-muted'
                  )}
                >
                  {selected ? <Check className="h-3 w-3 shrink-0" /> : <span className="w-3" />}
                  {item.symbol}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      <input
        type="number"
        inputMode="decimal"
        step="any"
        value={filter.value}
        onChange={(event) => onChange(filter.id, { value: parseMetricInput(event.target.value) })}
        className="h-8 w-[72px] rounded-lg border border-border bg-surface px-2 text-body-sm text-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
      />

      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove filter"
        className="rounded p-1 text-foreground-subtle hover:bg-surface-muted hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
